import { produce } from "immer";
import _ from "lodash";
import { bound } from "src/util/functions";
import { RecursivePartial } from "src/util/types";
import { getDefaultProductDetailsState } from "./defaults";
import {
  tpProductDetailsHandler,
  tProductDetailsAction,
  tProductDetailsAddQuantity,
  tProductDetailsBoundQuantity,
  tProductDetailsChangeQuantity,
  tProductDetailsDecrementQuantity,
  tProductDetailsState,
  tResetVariantSelectionAction,
  tSetDisplayImageAction,
  tSetProductDataAction,
  tSetTesterSelectedAction,
  tSetVariantSelectionAction,
} from "./types";

const handleSetProductDetailsData: tpProductDetailsHandler<tSetProductDataAction> = (_state, action) => {
  const aProductImages = (action.productData.images ?? [])
    .map((myImage) => {
      return myImage.image_url ?? "";
    })
    .filter((myImage) => myImage !== "");

  const aDefault = getDefaultProductDetailsState();
  const aVariant = action.productData.variants.find((variant) => variant.purchasable);
  const testerProduct = aVariant ? aVariant.tester : action.productData.tester;
  const testerPrice = testerProduct?.price ?? 1;
  const aIsAvailable = aVariant ? aVariant.purchasable : action.productData.availability === "available";
  const aWsp = aVariant ? aVariant.wholesalePrice : action.productData.wholesale_price;
  const aMsrp = aVariant ? aVariant.msrpPrice : action.productData.msrp_price;
  const aInventory = aVariant ? aVariant.inventory : action.productData.inventory;
  const variantStateProps = aVariant
    ? {
        variantSelections: aVariant?.options.reduce<{ [name: string]: string }>((acc, option) => {
          acc[option.name] = option.label;
          return acc;
        }, {}),
      }
    : {};

  return _.merge<tProductDetailsState, RecursivePartial<tProductDetailsState>>(aDefault, {
    productData: action.productData,
    productImages: aProductImages,
    displayImage: aProductImages[0] ?? "",
    quantity: action.productData.case_minimum,
    rawQuantity: action.productData.case_minimum,
    showTester: !!testerProduct,
    testerPrice: testerPrice,
    isAvailable: aIsAvailable,
    wsp: aWsp,
    msrp: aMsrp,
    inventory: aInventory,
    ...variantStateProps,
  });
};

const handleSetProductDetailsVariantSelection: tpProductDetailsHandler<tSetVariantSelectionAction> = (
  state,
  action,
) => {
  state = _.merge(state, {
    variantSelections: { [action.name]: action.label },
  });

  const aVariant = state.productData.variants.find((myVariant) => {
    return myVariant.options.every(({ name, label }) => {
      if (state.variantSelections && state.variantSelections[name]) {
        return state.variantSelections[name] === label;
      } else {
        return false;
      }
    });
  });

  if (aVariant !== undefined) {
    const aVariantImage = aVariant.imageUrl;

    if (aVariantImage) {
      state.productImages = [aVariantImage].concat(
        (state.productData.images ?? [])
          .map((myImage) => {
            return myImage.image_url ?? "";
          })
          .filter((myImage) => myImage !== ""),
      );

      state.displayImage = state.productImages[0] ?? "";
    } else {
      state.productImages = (state.productData.images ?? [])
        .map((myImage) => {
          return myImage.image_url ?? "";
        })
        .filter((myImage) => myImage !== "");

      if (!state.productImages.includes(state.displayImage)) {
        state.displayImage = state.productImages[0];
      }
    }

    state.showTester = aVariant.tester !== undefined;
    state.testerPrice = aVariant.tester?.price ?? 1;
    state.testerSelected = false;
    state.isAvailable = aVariant.purchasable;
    state.msrp = aVariant.msrpPrice;
    state.wsp = aVariant.wholesalePrice;
    state.inventory = aVariant.inventory;
  }

  return state;
};

const handleSetProductDetailsDisplayImage: tpProductDetailsHandler<tSetDisplayImageAction> = (state, action) => {
  if (typeof action.value === "string") {
    state.displayImage = action.value;
  } else {
    const aCurrIndex = state.productImages.indexOf(state.displayImage);

    if (aCurrIndex > -1 && aCurrIndex < state.productImages.length) {
      let aNewIndex = action.value(aCurrIndex);
      if (aNewIndex < 0) {
        aNewIndex = state.productImages.length - 1;
      } else if (aNewIndex > state.productImages.length - 1) {
        aNewIndex = 0;
      }

      state.displayImage = state.productImages[aNewIndex];
    }
  }

  return state;
};

const handleDecrementQuantity: tpProductDetailsHandler<tProductDetailsDecrementQuantity> = (state) => {
  const nextQuantity = Math.max(
    (state.rawQuantity ?? 0) - state.productData.case_minimum,
    state.productData.case_minimum,
  );

  state.rawQuantity = nextQuantity;
  state.quantity = nextQuantity;

  return state;
};

const handleAddQuantity: tpProductDetailsHandler<tProductDetailsAddQuantity> = (state) => {
  const nextQuantity = (state.rawQuantity ?? 0) + state.productData.case_minimum;

  if (nextQuantity > state.productData.inventory) return state;

  state.rawQuantity = nextQuantity;
  state.quantity = nextQuantity;

  return state;
};

const handleChangeQuantity: tpProductDetailsHandler<tProductDetailsChangeQuantity> = (state, action) => {
  const nextQuantity = action.payload;

  state.rawQuantity = nextQuantity;
  state.quantity = nextQuantity ?? 0;

  return state;
};

const handleBoundQuantity: tpProductDetailsHandler<tProductDetailsBoundQuantity> = (state) => {
  const rounded =
    Math.round((state.rawQuantity ?? 0) / state.productData.case_minimum) * state.productData.case_minimum;
  const nextQuantity = bound(state.productData.case_minimum, rounded, rounded);

  if (nextQuantity < state.productData.case_minimum) {
    state.rawQuantity = state.productData.case_minimum;
    state.quantity = state.productData.case_minimum;
    return state;
  }

  if (nextQuantity > state.productData.inventory) {
    const maxQuantity = state.productData.inventory - (state.productData.inventory % state.productData.case_minimum);
    state.rawQuantity = maxQuantity;
    state.quantity = maxQuantity;
    return state;
  }

  state.rawQuantity = nextQuantity;
  state.quantity = nextQuantity;
  return state;
};

const handleSetTesterSelected: tpProductDetailsHandler<tSetTesterSelectedAction> = (state, action) => {
  if (typeof action.value === "boolean") {
    state.testerSelected = action.value;
  } else {
    state.testerSelected = action.value(state.testerSelected);
  }

  return state;
};
const handleResetProductDetailsVariantSelection: tpProductDetailsHandler<tResetVariantSelectionAction> = (
  state,
  action,
) => {
  state.variantSelections = {};
  state.variantSelections = { [action.name]: action.label };
  state.isAvailable = false;
  state.inventory = 0;
  return state;
};

type tpProductDetailsReducer = (state: tProductDetailsState, action: tProductDetailsAction) => tProductDetailsState;
export const ProductDetailsReducer: tpProductDetailsReducer = produce(
  (state = getDefaultProductDetailsState(), action) => {
    switch (action.type) {
      case "SET_PRODUCT_DETAILS_DATA":
        return handleSetProductDetailsData(state, action);
      case "SET_PRODUCT_DETAILS_VARIANT_SELECTION":
        return handleSetProductDetailsVariantSelection(state, action);
      case "SET_PRODUCT_DETAILS_DISPLAY_IMAGE":
        return handleSetProductDetailsDisplayImage(state, action);
      case "PRODUCT_DETAILS_DECREMENT_QUANTITY":
        return handleDecrementQuantity(state, action);
      case "PRODUCT_DETAILS_ADD_QUANTITY":
        return handleAddQuantity(state, action);
      case "PRODUCT_DETAILS_CHANGE_QUANTITY":
        return handleChangeQuantity(state, action);
      case "SET_PRODUCT_DETAILS_BOUND_QUANTITY":
        return handleBoundQuantity(state, action);
      case "SET_PRODUCT_DETAILS_TESTER":
        return handleSetTesterSelected(state, action);
      case "RESET_PRODUCT_DETAILS_VARIANT_SELECTION":
        return handleResetProductDetailsVariantSelection(state, action);
      default:
        return state;
    }
  },
);
